import { useState, useEffect, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sparkles,
  LayoutDashboard,
  Receipt,
  Target,
  Snowflake,
  Wallet,
  FileText,
  Bot,
  Lightbulb,
  TrendingUp,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Calculator,
  Shield,
} from 'lucide-react';

interface UserGuideTourProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Restrict which steps are shown (by id). Defaults to all steps (full guide). */
  stepIds?: readonly string[];
  /** Called when the user finishes the last step (used to chain the onboarding flow). */
  onComplete?: () => void;
  /** Label for the final button. Defaults to "¡Empezar!". */
  finishLabel?: string;
}

interface GuideStep {
  id: string;
  icon: typeof Sparkles;
  badge: string;
  title: string;
  description: string;
  content: ReactNode;
}

const STORAGE_KEY = 'foco-guide-completed-v1';
const SKIP_KEY = 'foco-guide-skip';

const steps: GuideStep[] = [
  {
    id: 'welcome',
    icon: Sparkles,
    badge: 'Bienvenida',
    title: '¡Bienvenido a Tu Dinero, Tu Poder!',
    description: 'Esta guía te enseñará a usar la app y sacarle el máximo provecho.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Esta plataforma te ayuda a tomar el control de tu vida financiera con
          herramientas inteligentes y un sistema de evaluación llamado{' '}
          <strong className="text-primary">FOCO Score</strong>.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border p-3 bg-primary/5">
            <p className="text-xs font-semibold text-primary mb-1">📊 Visibilidad</p>
            <p className="text-sm text-muted-foreground">
              Mide tus ingresos, gastos y ahorros mes a mes.
            </p>
          </div>
          <div className="rounded-lg border p-3 bg-success/5">
            <p className="text-xs font-semibold text-success mb-1">🎯 Estrategia</p>
            <p className="text-sm text-muted-foreground">
              Define metas y un plan para pagar deudas.
            </p>
          </div>
          <div className="rounded-lg border p-3 bg-warning/5">
            <p className="text-xs font-semibold text-warning mb-1">⚠️ Alertas</p>
            <p className="text-sm text-muted-foreground">
              Recibe avisos cuando algo se sale de control.
            </p>
          </div>
          <div className="rounded-lg border p-3 bg-accent/5">
            <p className="text-xs font-semibold text-accent mb-1">🤖 Asesor IA</p>
            <p className="text-sm text-muted-foreground">
              Recibe consejos personalizados de IA.
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground italic">
          Toma 3 minutos. Puedes reabrir esta guía cuando quieras desde el botón de
          ayuda.
        </p>
      </div>
    ),
  },
  {
    id: 'score',
    icon: LayoutDashboard,
    badge: 'Paso 1',
    title: 'Panel Principal y FOCO Score',
    description: 'Tu centro de control financiero mensual.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          El <strong>Panel Principal</strong> es lo primero que ves al entrar.
          Muestra el resumen del mes en curso.
        </p>
        <div className="rounded-lg border-l-4 border-primary bg-primary/5 p-4">
          <p className="font-semibold text-primary mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Tu FOCO Score (0-100)
          </p>
          <ul className="text-sm space-y-1.5 text-muted-foreground">
            <li>
              <Badge variant="destructive" className="mr-2">0-40</Badge>
              <strong>Zona Crítica:</strong> reorganiza gastos urgentemente.
            </li>
            <li>
              <Badge className="mr-2 bg-warning text-warning-foreground">41-60</Badge>
              <strong>Zona de Riesgo:</strong> mejora hábitos clave.
            </li>
            <li>
              <Badge className="mr-2 bg-success text-success-foreground">61-80</Badge>
              <strong>Zona de Avance:</strong> vas por buen camino.
            </li>
            <li>
              <Badge className="mr-2 bg-accent text-accent-foreground">81-100</Badge>
              <strong>Zona de Libertad:</strong> ¡salud financiera sólida!
            </li>
          </ul>
        </div>
        <p className="text-sm">
          💡 <strong>Tip:</strong> el FOCO Score se calcula con tu tasa de ahorro,
          ratio de deuda y proporción de gastos fijos vs variables.
        </p>
      </div>
    ),
  },
  {
    id: 'transactions',
    icon: Receipt,
    badge: 'Paso 2',
    title: 'Registrar Transacciones',
    description: 'La base de todo: tus ingresos y gastos del día a día.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Ve a <strong>Transacciones</strong> y registra cada movimiento. Mientras
          más completo esté, más útil será tu FOCO Score.
        </p>
        <div className="space-y-2">
          <p className="text-sm font-semibold">Tipos de transacción:</p>
          <div className="grid gap-2 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-success">💰</span>
              <span>
                <strong>Ingreso:</strong> salario, ventas, regalos
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-destructive">🏠</span>
              <span>
                <strong>Gasto fijo:</strong> alquiler, internet, suscripciones
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-warning">🛒</span>
              <span>
                <strong>Gasto variable:</strong> comida, entretenimiento
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-primary">🐷</span>
              <span>
                <strong>Ahorro/Inversión:</strong> aportes a tus metas
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-accent">💳</span>
              <span>
                <strong>Pago de deuda:</strong> tarjetas, préstamos
              </span>
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-muted p-3 text-sm">
          ⚡ <strong>Truco:</strong> usa la opción <em>Importar</em> para subir un
          estado de cuenta bancario y ahorrarte tiempo.
        </div>
      </div>
    ),
  },
  {
    id: 'budgets',
    icon: Wallet,
    badge: 'Paso 3',
    title: 'Presupuestos por Categoría',
    description: 'Pon límites mensuales y recibe alertas cuando te acerques.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Define cuánto puedes gastar al mes en cada categoría (comida, transporte,
          ocio, etc.). La app te avisa al llegar al 80% y al 100%.
        </p>
        <div className="rounded-lg border p-4 bg-gradient-to-br from-primary/5 to-success/5">
          <p className="font-semibold mb-2 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-warning" />
            Regla 50/30/20 (recomendada)
          </p>
          <ul className="text-sm space-y-1 text-muted-foreground">
            <li>
              <strong className="text-foreground">50%</strong> a necesidades (vivienda,
              comida, transporte)
            </li>
            <li>
              <strong className="text-foreground">30%</strong> a deseos
              (entretenimiento, restaurantes)
            </li>
            <li>
              <strong className="text-foreground">20%</strong> a ahorro y pago de
              deudas
            </li>
          </ul>
        </div>
        <p className="text-sm">
          🎯 Empieza con presupuestos realistas según tus últimos 3 meses, y
          ajústalos cada mes.
        </p>
      </div>
    ),
  },
  {
    id: 'goals',
    icon: Target,
    badge: 'Paso 4',
    title: 'Metas Financieras',
    description: 'Define objetivos claros y mide tu progreso.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          En <strong>Metas</strong> puedes crear objetivos como un fondo de
          emergencia, un viaje, una casa o tu retiro.
        </p>
        <div className="space-y-2">
          <p className="text-sm font-semibold">Buenas prácticas (método SMART):</p>
          <ul className="text-sm space-y-1.5 text-muted-foreground list-disc list-inside">
            <li>
              <strong>Específica:</strong> "Fondo de emergencia de ₡1.500.000"
            </li>
            <li>
              <strong>Medible:</strong> con monto y fecha límite
            </li>
            <li>
              <strong>Alcanzable:</strong> según tu capacidad de ahorro real
            </li>
            <li>
              <strong>Relevante:</strong> alineada a tu vida
            </li>
            <li>
              <strong>Temporal:</strong> con deadline
            </li>
          </ul>
        </div>
        <div className="rounded-lg bg-success/5 border border-success/20 p-3 text-sm">
          🏆 <strong>Prioridad #1:</strong> antes que cualquier otra meta, construye
          un fondo de emergencia de 3 a 6 meses de gastos.
        </div>
      </div>
    ),
  },
  {
    id: 'snowball',
    icon: Snowflake,
    badge: 'Paso 5',
    title: 'Método Bola de Nieve',
    description: 'La estrategia más efectiva (psicológicamente) para salir de deudas.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          En <strong>Bola de Nieve</strong> registra todas tus deudas. La app las
          ordena de menor a mayor y te muestra una proyección visual.
        </p>
        <div className="rounded-lg border p-4 space-y-2 bg-card">
          <p className="font-semibold flex items-center gap-2">
            <Calculator className="h-4 w-4 text-primary" />
            Cómo funciona
          </p>
          <ol className="text-sm space-y-1.5 text-muted-foreground list-decimal list-inside">
            <li>Paga el <strong>mínimo</strong> en todas las deudas</li>
            <li>
              Destina todo el extra a la <strong>deuda más pequeña</strong>
            </li>
            <li>
              Cuando la pagas, "ruedas" ese pago a la siguiente
            </li>
            <li>El efecto se acelera con cada victoria 🎉</li>
          </ol>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded bg-success/10 p-2 text-center">
            <Trophy className="h-4 w-4 text-success mx-auto mb-1" />
            <p className="font-semibold">Victorias rápidas</p>
            <p className="text-muted-foreground">Te mantienen motivado</p>
          </div>
          <div className="rounded bg-primary/10 p-2 text-center">
            <TrendingUp className="h-4 w-4 text-primary mx-auto mb-1" />
            <p className="font-semibold">Proyección visual</p>
            <p className="text-muted-foreground">Ves cuándo serás libre</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'reports',
    icon: FileText,
    badge: 'Paso 6',
    title: 'Reportes y Panel Anual',
    description: 'Visualiza tu evolución a lo largo del tiempo.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Las secciones <strong>Reportes</strong> y <strong>Panel Anual</strong> te
          muestran gráficos comparativos por mes, categoría y tipo de gasto.
        </p>
        <div className="space-y-2 text-sm">
          <p className="font-semibold">Úsalas para:</p>
          <ul className="space-y-1.5 text-muted-foreground list-disc list-inside">
            <li>Identificar meses problemáticos o categorías que se disparan</li>
            <li>Ver tu tasa de ahorro real a lo largo del año</li>
            <li>Exportar tu información (PDF) para tu contador o asesor</li>
            <li>Tomar decisiones con datos, no con suposiciones</li>
          </ul>
        </div>
        <div className="rounded-lg bg-muted p-3 text-sm">
          📅 <strong>Ritual mensual sugerido:</strong> el primer fin de semana de cada
          mes, dedica 20 minutos a revisar tus reportes y ajustar presupuestos.
        </div>
      </div>
    ),
  },
  {
    id: 'ai-advisor',
    icon: Bot,
    badge: 'Paso 7',
    title: 'Asesor Financiero IA',
    description: 'Tu coach personal disponible 24/7.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          El botón flotante con la chispa{' '}
          <Sparkles className="inline h-4 w-4 text-accent" /> abre tu{' '}
          <strong>Asesor IA</strong>. Conoce tus números y responde con consejos
          personalizados.
        </p>
        <div className="space-y-2">
          <p className="text-sm font-semibold">Ejemplos de preguntas que puedes hacer:</p>
          <div className="space-y-1.5 text-sm">
            <div className="rounded-lg bg-accent/10 p-2 italic">
              "¿Cómo puedo mejorar mi FOCO Score este mes?"
            </div>
            <div className="rounded-lg bg-accent/10 p-2 italic">
              "¿En qué categoría estoy gastando demasiado?"
            </div>
            <div className="rounded-lg bg-accent/10 p-2 italic">
              "¿Cuánto debería ahorrar para mi fondo de emergencia?"
            </div>
            <div className="rounded-lg bg-accent/10 p-2 italic">
              "Dame un plan para salir de mi deuda de tarjeta de crédito."
            </div>
          </div>
        </div>
        <div className="rounded-lg border-l-4 border-warning bg-warning/5 p-3 text-xs">
          🔒 <strong>Privacidad:</strong> debes aceptar el uso de IA antes de chatear.
          Puedes revocar el consentimiento cuando quieras desde Configuración.
        </div>
      </div>
    ),
  },
  {
    id: 'settings',
    icon: Shield,
    badge: 'Paso 8',
    title: 'Configuración y Privacidad',
    description: 'Personaliza la app y controla tus datos.',
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          En <strong>Configuración</strong> puedes ajustar:
        </p>
        <div className="grid gap-2 text-sm">
          <div className="rounded-lg border p-2">
            🌐 <strong>Moneda</strong> por defecto (CRC, USD, EUR…)
          </div>
          <div className="rounded-lg border p-2">
            🎨 <strong>Tema</strong> claro/oscuro y colores de gráficos
          </div>
          <div className="rounded-lg border p-2">
            🔔 <strong>Notificaciones</strong> (alertas de presupuesto, deudas, metas)
          </div>
          <div className="rounded-lg border p-2">
            🔐 <strong>Biometría</strong> para inicio de sesión rápido
          </div>
          <div className="rounded-lg border p-2">
            🛡️ <strong>Centro de privacidad:</strong> exportar o eliminar tus datos
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'ready',
    icon: CheckCircle2,
    badge: 'Listo',
    title: '¡Estás listo para empezar!',
    description: 'Ahora tienes el conocimiento para sacarle el máximo provecho.',
    content: (
      <div className="space-y-4">
        <div className="rounded-lg border-2 border-success bg-success/5 p-4 text-center">
          <Trophy className="h-10 w-10 text-success mx-auto mb-2" />
          <p className="font-semibold text-lg">Tu plan de los próximos 7 días</p>
        </div>
        <ol className="space-y-2 text-sm">
          <li className="flex gap-3">
            <span className="font-bold text-primary">1.</span>
            <span>
              Registra tus <strong>ingresos del mes</strong> y los gastos de los
              últimos 7 días.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-primary">2.</span>
            <span>
              Crea tu <strong>primera meta</strong> (sugerencia: fondo de emergencia
              pequeño, ej. ₡200.000).
            </span>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-primary">3.</span>
            <span>
              Si tienes deudas, agrégalas todas en <strong>Bola de Nieve</strong>.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-primary">4.</span>
            <span>
              Define <strong>2-3 presupuestos</strong> en las categorías donde más
              gastas.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-primary">5.</span>
            <span>
              Pregúntale al <strong>Asesor IA</strong> qué hacer para subir tu FOCO
              Score.
            </span>
          </li>
        </ol>
        <div className="rounded-lg bg-gradient-to-r from-primary/10 to-success/10 p-4 text-center text-sm">
          <p className="font-semibold mb-1">💡 Recuerda</p>
          <p className="text-muted-foreground">
            La libertad financiera no se logra en un día, sino con{' '}
            <strong>pequeños hábitos consistentes</strong>. ¡Tú puedes!
          </p>
        </div>
      </div>
    ),
  },
];

export function UserGuideTour({
  open,
  onOpenChange,
  stepIds,
  onComplete,
  finishLabel,
}: UserGuideTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const visibleSteps = stepIds
    ? stepIds.map((id) => steps.find((s) => s.id === id)).filter((s): s is GuideStep => Boolean(s))
    : steps;

  // Onboarding mode: the parent flow controls completion, so hide the skip checkbox.
  const isOnboardingMode = Boolean(onComplete);

  useEffect(() => {
    if (open) setCurrentStep(0);
  }, [open]);

  const step = visibleSteps[currentStep] ?? visibleSteps[0];
  const Icon = step.icon;
  const isFirst = currentStep === 0;
  const isLast = currentStep === visibleSteps.length - 1;
  const progress = ((currentStep + 1) / visibleSteps.length) * 100;

  const finishFlow = () => {
    if (isOnboardingMode) {
      onComplete?.();
      return;
    }
    localStorage.setItem(STORAGE_KEY, 'true');
    onOpenChange(false);
  };

  const handleClose = () => {
    if (isOnboardingMode) {
      onComplete?.();
      return;
    }
    if (dontShowAgain) {
      localStorage.setItem(SKIP_KEY, 'true');
    }
    if (isLast) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleClose();
      return;
    }

    onOpenChange(true);
  };

  const handleNext = () => {
    if (isLast) {
      finishFlow();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start gap-3 mb-3">
            <div className="rounded-lg bg-primary/10 p-2.5 flex-shrink-0">
              <Icon className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <Badge variant="secondary" className="mb-1.5">
                {step.badge} · {currentStep + 1}/{visibleSteps.length}
              </Badge>
              <DialogTitle className="text-xl leading-tight">{step.title}</DialogTitle>
              <DialogDescription className="mt-1">
                {step.description}
              </DialogDescription>
            </div>
          </div>
          <Progress value={progress} className="h-1.5" />
        </DialogHeader>

        <ScrollArea className="flex-1 px-6 py-5 max-h-[55vh]">
          {step.content}
        </ScrollArea>

        <div className="px-6 py-4 border-t bg-muted/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {isOnboardingMode ? (
            <div className="hidden sm:block" />
          ) : (
            <div className="flex items-center gap-2">
              <Checkbox
                id="dont-show"
                checked={dontShowAgain}
                onCheckedChange={(c) => setDontShowAgain(c === true)}
              />
              <label
                htmlFor="dont-show"
                className="text-xs text-muted-foreground cursor-pointer select-none"
              >
                No mostrar al iniciar sesión
              </label>
            </div>
          )}
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentStep((s) => s - 1)}
              disabled={isFirst}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </Button>
            <Button size="sm" onClick={handleNext}>
              {isLast ? (
                <>
                  {finishLabel ?? '¡Empezar!'}
                  <CheckCircle2 className="h-4 w-4 ml-1" />
                </>
              ) : (
                <>
                  Siguiente
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function shouldAutoShowGuide(): boolean {
  if (typeof window === 'undefined') return false;
  if (localStorage.getItem(SKIP_KEY) === 'true') return false;
  if (localStorage.getItem(STORAGE_KEY) === 'true') return false;
  return true;
}
